import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  Grid,
  PureTextButton,
  Stack,
  Text,
  styled,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useReportMonitors, useSession } from '@ltvco/refresh-lib/v1';
import { IdentityCard, FootprintCard } from '@ltvco/refresh-lib/v2';
import {
  DirectionsCar,
  Email,
  Help,
  Home,
  PetsRounded,
  PhoneIphone,
} from '@mui/icons-material';
import { useMemo, useState } from 'react';

import { ManageReportMonitorsModal } from 'components/idMonitor/ClaimedReportMonitorsContainer/ManageReportMonitorsModal';
import { filterAndSortReports } from 'routes/Dashboard/IdentityHub/utils';

import { CustomModal } from './CustomModal';

const StyledStack = styled(Stack)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

function DashboardDigitialFoorprint() {
  const { isMobile } = useScreenSize();
  const { currentMonitors } = useReportMonitors();
  const {
    session: { account },
  } = useSession();
  const { isHidden } = useSubscriptionAccessConfigContext();

  const { first_name, last_name } = account?.account?.user_info || {};

  const [footprintModalOpen, setFootprintModalOpen] = useState(false);
  const [footprintModalTitle, setFootprintModalTitle] = useState<
    JSX.Element | undefined
  >();
  const [footprintModalDescription, setFootprintModalDescription] =
    useState('');

  const filteredAndSortedReports = useMemo(
    () => filterAndSortReports(currentMonitors || []),
    [currentMonitors]
  );
  const claimedPersonReports =
    filteredAndSortedReports['detailed_person_report'];
  const claimedPropertyReports = filteredAndSortedReports['property_report'];
  const claimedVehicleReports = filteredAndSortedReports['vehicle_report'];
  const claimedEmailReports = filteredAndSortedReports['social_network_report'];
  const claimedPhoneReports = filteredAndSortedReports['reverse_phone_report'];

  const footPrintReportTypes = [
    'person',
    'phone',
    'vehicle',
    'email',
    'property',
  ];

  const hideDashboardDigitalFootprint = footPrintReportTypes.some((type) =>
    isHidden(type)
  );

  const REPORTVARIATION = {
    phone: {
      title: 'Phone Number',
      icon: <PhoneIphone sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'phone',
      claimedReports: claimedPhoneReports,
    },
    email: {
      title: 'Email Address',
      icon: <Email sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'email',
      claimedReports: claimedEmailReports,
    },
    vehicle: {
      title: 'Vehicle',
      icon: <DirectionsCar sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'vehicle',
      claimedReports: claimedVehicleReports,
    },
    property: {
      title: 'Home Address',
      icon: <Home sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'property',
      claimedReports: claimedPropertyReports,
    },
  };

  function handleClickWhatIsDigitalFootprint() {
    setFootprintModalTitle(
      <Text textAlign="center" fontWeight="normal" variant="h2" m={0}>
        <PetsRounded
          sx={{
            fontSize: '24px',
            color: '#0052FF',
            marginRight: 2,
          }}
        />
        Your Digital Footprint
      </Text>
    );
    setFootprintModalDescription(
      'Your digital footprint is composed of data that is publicly available on the internet. BeenVerified allows you to monitor this data for changes, or when new information becomes available.'
    );
    setFootprintModalOpen(!footprintModalOpen);
  }

  return (
    <>
      {!hideDashboardDigitalFootprint && (
        <>
          <StyledStack direction="row" gap={1}>
            <PetsRounded
              sx={{
                fontSize: '24px',
                color: '#0052FF',
              }}
            />
            <Text variant="h3" margin={0}>
              Your Digital Footprint
            </Text>

            <PureTextButton
              onClick={() => handleClickWhatIsDigitalFootprint()}
              sx={{
                '&.MuiButton-root': {
                  padding: 0,
                },
              }}
            >
              <Help fontSize="small" sx={{ mr: 1 }} />
            </PureTextButton>
          </StyledStack>

          <StyledStack
            width="100%"
            gap={1}
            minHeight={isMobile ? 'auto' : '240px'}
            direction={isMobile ? 'column' : 'row'}
            marginTop={2}
            marginBottom={3}
            sx={{
              alignItems: 'stretch',
            }}
          >
            <IdentityCard
              firstName={first_name || ''}
              lastName={last_name || ''}
              claimedPersonReports={claimedPersonReports}
            />
            <Grid
              container
              spacing={1}
              width={isMobile ? '100%' : '60%'}
              flexWrap="wrap"
            >
              {Object.values(REPORTVARIATION).map((item: any) => (
                <Grid key={item?.type} size={{ xs: 12, sm: 6 }}>
                  <FootprintCard
                    title={item?.title}
                    icon={item?.icon}
                    type={item?.type}
                    claimedReports={item?.claimedReports}
                    Modal={ManageReportMonitorsModal}
                  />
                </Grid>
              ))}
            </Grid>
          </StyledStack>
        </>
      )}

      <CustomModal
        open={footprintModalOpen}
        customTitle={footprintModalTitle}
        description={footprintModalDescription}
        handleClose={() => setFootprintModalOpen(!footprintModalOpen)}
        returnButtonText="Return to Dashboard"
      />
    </>
  );
}

export default DashboardDigitialFoorprint;
